import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  getExperimentEventHistory,
  getExperimentSnapshot,
  subscribeToExperimentEvents,
} from '../../shared/utilities/amplitudeExperiment';
import { selectExperimentsReady } from '../ExperimentGate/useExperiment';

const STORAGE_KEY = 'insomnia.experimentDebugPanel';

const readPanelFlagFromUrl = () => {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }
  try {
    const value = new URLSearchParams(window.location.search).get('debug_exp');
    if (value === '1') return true;
    if (value === '0') return false;
    return null;
  } catch {
    return null;
  }
};

const readPanelFlagFromStorage = () => {
  try {
    return window.sessionStorage?.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writePanelFlagToStorage = (enabled) => {
  try {
    if (enabled) {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
};

const isProduction = () =>
  typeof window !== 'undefined' &&
  window.environment?.REACT_APP_ENV === 'production';

const shouldRenderPanel = () => {
  if (isProduction()) {
    writePanelFlagToStorage(false);
    return false;
  }
  const urlFlag = readPanelFlagFromUrl();
  if (urlFlag != null) {
    writePanelFlagToStorage(urlFlag);
    return urlFlag;
  }
  return readPanelFlagFromStorage();
};

const formatTime = (ms) => {
  const d = new Date(ms);
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const formatJSON = (value) => {
  if (value == null) return '—';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const eventColor = (type) => {
  if (type === 'fetch:error' || type === 'init:error') return '#ff6b6b';
  if (type === 'fetch:success') return '#51cf66';
  if (type === 'expose') return '#ffd43b';
  if (type === 'fetch:start') return '#74c0fc';
  if (type === 'init') return '#b197fc';
  if (type === 'override') return '#ffa94d';
  return '#dee2e6';
};

const panelStyles = {
  container: {
    position: 'fixed',
    bottom: 16,
    right: 16,
    zIndex: 2147483647,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 12,
    color: '#f1f3f5',
    background: 'rgba(20, 22, 28, 0.96)',
    border: '1px solid #495057',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
    width: 'min(860px, 92vw)',
    height: 'min(88vh, 960px)',
    display: 'flex',
    flexDirection: 'column',
  },
  collapsed: {
    padding: '6px 10px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid #343a40',
    background: 'rgba(33, 37, 41, 0.95)',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  title: { fontWeight: 600, fontSize: 13 },
  pillRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 },
  pill: (ok) => ({
    padding: '2px 6px',
    borderRadius: 4,
    background: ok ? 'rgba(81, 207, 102, 0.18)' : 'rgba(255, 107, 107, 0.18)',
    color: ok ? '#8ce99a' : '#ffa8a8',
    border: `1px solid ${ok ? '#37b24d' : '#e03131'}`,
    fontSize: 11,
  }),
  tabs: { display: 'flex', borderBottom: '1px solid #343a40' },
  tab: (active) => ({
    padding: '6px 12px',
    cursor: 'pointer',
    background: active ? 'rgba(73, 80, 87, 0.6)' : 'transparent',
    borderRight: '1px solid #343a40',
    color: active ? '#fff' : '#adb5bd',
  }),
  body: {
    overflow: 'auto',
    padding: 8,
    flex: 1,
  },
  expCard: {
    border: '1px solid #343a40',
    borderRadius: 6,
    padding: 8,
    marginBottom: 8,
    background: 'rgba(33, 37, 41, 0.6)',
  },
  expRow: { display: 'flex', justifyContent: 'space-between', gap: 8 },
  expLabel: { color: '#adb5bd', minWidth: 110 },
  expValue: { wordBreak: 'break-all', color: '#f8f9fa' },
  variantBadge: (exposed) => ({
    padding: '1px 6px',
    borderRadius: 4,
    background: exposed ? 'rgba(255, 212, 59, 0.18)' : 'rgba(173, 181, 189, 0.18)',
    color: exposed ? '#ffe066' : '#ced4da',
    border: `1px solid ${exposed ? '#f59f00' : '#495057'}`,
    fontSize: 11,
  }),
  eventRow: {
    display: 'grid',
    gridTemplateColumns: '88px 110px 1fr',
    gap: 6,
    padding: '4px 6px',
    borderBottom: '1px solid #2b3035',
    alignItems: 'baseline',
  },
  closeBtn: {
    background: 'transparent',
    color: '#adb5bd',
    border: '1px solid #495057',
    borderRadius: 4,
    padding: '2px 8px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

const snapshotShape = PropTypes.shape({
  isClientReady: PropTypes.bool.isRequired,
  hasFetched: PropTypes.bool.isRequired,
  isOverrideDisabled: PropTypes.bool.isRequired,
});

const StatusPills = ({ snapshot, isReady }) => (
  <div style={panelStyles.pillRow}>
    <span style={panelStyles.pill(snapshot.isClientReady)}>
      client
      {' '}
      {snapshot.isClientReady ? 'ready' : 'missing'}
    </span>
    <span style={panelStyles.pill(snapshot.hasFetched)}>
      fetch
      {' '}
      {snapshot.hasFetched ? 'done' : 'pending'}
    </span>
    <span style={panelStyles.pill(isReady)}>
      redux
      {' '}
      {isReady ? 'ready' : 'pending'}
    </span>
    <span style={panelStyles.pill(!snapshot.isOverrideDisabled)}>
      {snapshot.isOverrideDisabled ? 'override OFF' : 'override ON'}
    </span>
  </div>
);

StatusPills.propTypes = {
  snapshot: snapshotShape.isRequired,
  isReady: PropTypes.bool.isRequired,
};

const experimentEntryShape = PropTypes.shape({
  experimentId: PropTypes.string.isRequired,
  flagKey: PropTypes.string.isRequired,
  fallbackVariant: PropTypes.string.isRequired,
  assignedVariant: PropTypes.string,
  assignedPayload: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  exposed: PropTypes.bool.isRequired,
});

const experimentEventShape = PropTypes.shape({
  type: PropTypes.string.isRequired,
  at: PropTypes.number.isRequired,
});

const ExperimentRow = ({ entry }) => {
  const displayVariant =
    entry.assignedVariant ?? (entry.exposed ? '(null)' : entry.fallbackVariant);
  return (
    <div style={panelStyles.expCard}>
      <div style={panelStyles.expRow}>
        <strong style={panelStyles.expValue}>{entry.experimentId}</strong>
        <span style={panelStyles.variantBadge(entry.exposed)}>
          {entry.exposed ? 'exposed' : 'fallback'}
        </span>
      </div>
      <div style={panelStyles.expRow}>
        <span style={panelStyles.expLabel}>flag</span>
        <span style={panelStyles.expValue}>{entry.flagKey}</span>
      </div>
      <div style={panelStyles.expRow}>
        <span style={panelStyles.expLabel}>variant</span>
        <span style={panelStyles.expValue}>{displayVariant}</span>
      </div>
      <div style={panelStyles.expRow}>
        <span style={panelStyles.expLabel}>fallback</span>
        <span style={panelStyles.expValue}>{entry.fallbackVariant}</span>
      </div>
      <div style={panelStyles.expRow}>
        <span style={panelStyles.expLabel}>payload</span>
        <span style={panelStyles.expValue}>{formatJSON(entry.assignedPayload)}</span>
      </div>
    </div>
  );
};

ExperimentRow.propTypes = {
  entry: experimentEntryShape.isRequired,
};

const EventRow = ({ event }) => {
  const details = { ...event };
  delete details.type;
  delete details.at;
  return (
    <div style={panelStyles.eventRow}>
      <span style={{ color: '#868e96' }}>{formatTime(event.at)}</span>
      <span style={{ color: eventColor(event.type), fontWeight: 600 }}>
        {event.type}
      </span>
      <span style={{ color: '#dee2e6' }}>{formatJSON(details)}</span>
    </div>
  );
};

EventRow.propTypes = {
  event: experimentEventShape.isRequired,
};

const ExperimentDebugPanel = () => {
  const [enabled, setEnabled] = useState(shouldRenderPanel);
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState('state');
  const [snapshot, setSnapshot] = useState(() => getExperimentSnapshot());
  const [events, setEvents] = useState(() => getExperimentEventHistory());
  const eventsEndRef = useRef(null);

  const reduxIsReady = useSelector(selectExperimentsReady);

  const refresh = useCallback(() => {
    setSnapshot(getExperimentSnapshot());
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const unsubscribe = subscribeToExperimentEvents((event) => {
      setEvents((prev) => {
        const next = prev.concat(event);
        return next.length > 200 ? next.slice(next.length - 200) : next;
      });
      refresh();
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [enabled, refresh]);

  useEffect(() => {
    refresh();
  }, [reduxIsReady, refresh]);

  useEffect(() => {
    if (tab === 'events' && eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ block: 'end' });
    }
  }, [events, tab]);

  const clearEvents = useCallback(() => setEvents([]), []);
  const close = useCallback(() => {
    writePanelFlagToStorage(false);
    setEnabled(false);
  }, []);

  const summaryLabel = useMemo(() => {
    const ready = snapshot.hasFetched ? '●' : '○';
    return `exp ${ready} ${snapshot.experiments.length}`;
  }, [snapshot]);

  if (!enabled) return null;

  if (!open) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 2147483647,
          fontFamily: panelStyles.container.fontFamily,
          fontSize: 12,
          color: '#f1f3f5',
          background: 'rgba(20, 22, 28, 0.96)',
          border: '1px solid #495057',
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
          padding: '6px 12px',
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
        title="Open experiment debug panel"
      >
        <span role="img" aria-label="experiment">🧪</span> 
        {' '}
        {summaryLabel}
      </div>
    );
  }

  return (
    <div style={panelStyles.container}>
      <div style={panelStyles.header}>
        <div>
          <div style={panelStyles.title}>Experiment Debug</div>
          <StatusPills snapshot={snapshot} isReady={reduxIsReady} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            style={panelStyles.closeBtn}
            onClick={() => setOpen(false)}
            title="Collapse"
          >
            —
          </button>
          <button
            type="button"
            style={panelStyles.closeBtn}
            onClick={close}
            title="Hide (?debug_exp=1 to bring back)"
          >
            ×
          </button>
        </div>
      </div>
      <div style={panelStyles.tabs}>
        <div
          style={panelStyles.tab(tab === 'state')}
          onClick={() => setTab('state')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setTab('state');
          }}
        >
          State
        </div>
        <div
          style={panelStyles.tab(tab === 'events')}
          onClick={() => setTab('events')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setTab('events');
          }}
        >
          Events (
          {events.length}
)
        </div>
        {tab === 'events' && (
          <div style={{ marginLeft: 'auto', padding: '4px 6px' }}>
            <button
              type="button"
              style={panelStyles.closeBtn}
              onClick={clearEvents}
            >
              clear
            </button>
          </div>
        )}
      </div>
      <div style={panelStyles.body}>
        {tab === 'state' &&
          snapshot.experiments.map((entry) => (
            <ExperimentRow key={entry.experimentId} entry={entry} />
          ))}
        {tab === 'events' && (
          <div>
            {events.length === 0 ? (
              <div style={{ color: '#868e96', padding: 8 }}>No events yet.</div>
            ) : (
              events.map((event) => (
                <EventRow
                  key={`${event.at}-${event.type}`}
                  event={event}
                />
              ))
            )}
            <div ref={eventsEndRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperimentDebugPanel;
